import React, { useRef, useState, useEffect } from 'react';
import { Download } from 'lucide-react';
import clip from '../../assets/img/clip.png';
import { parseCandidateDocUrl } from '../../utils/formatters';

const DocumentUpload = ({ t, initialDocuments = [], onDocumentSelect, activeDocId, onChange, authAxios }) => {
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setFiles(initialDocuments ?? []);
    onChange?.(initialDocuments ?? []);
  }, [initialDocuments, onChange]);

  const handleDivClick = () => fileInputRef.current.click();

  const processFiles = (newFiles) => {
    const pdfFiles = newFiles.filter(f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));

    if (pdfFiles.length > 0) {
      const newDocs = pdfFiles.map(file => ({
        id: `local-${Math.random()}`,
        name: file.name,
        url: URL.createObjectURL(file),
        file,
      }));
      const updated = [...files, ...newDocs];
      setFiles(updated);
      onChange?.(updated);
      if (onDocumentSelect) onDocumentSelect(newDocs[0]);
    }
  };

  const removeFile = (e, idToRemove) => {
    e.stopPropagation();
    setFiles((prev) => {
      const toRemove = prev.find((f) => f.id === idToRemove);

      if (toRemove?.url?.startsWith("blob:")) {
        URL.revokeObjectURL(toRemove.url);
      }

      const next = prev.filter((f) => f.id !== idToRemove);
      onChange?.(next);
      return next;
    });

    if (activeDocId === idToRemove && onDocumentSelect) onDocumentSelect(null);
  };

  const getPresignedUrl = async (url, mode) => {
    if (!url) return null;
    if (url.startsWith("blob:") || url.startsWith("http")) return url;
    const parsed = parseCandidateDocUrl(url);
    if (!parsed || !authAxios) return null;
    try {
      const { data } = await authAxios.get(
        `/candidates/${parsed.candidateId}/documents/${parsed.fileName}/presign`,
        { params: { mode } }
      );
      return data?.url ?? null;
    } catch {
      return null;
    }
  };

  const handleFileClick = async (file) => {
    if (window.innerWidth < 1024) {
      const url = await getPresignedUrl(file.url, "preview");
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      if (onDocumentSelect) onDocumentSelect(file);
    }
  };

  const handleMobileDownload = async (e, file) => {
    e.stopPropagation();
    const url = await getPresignedUrl(file.url, "download");
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className="mb-8">
      <h2 className="text-xl font-semibold text-darkGray mb-2">{t('FORM.DOCUMENTS.TITLE')}</h2>

      <div
        onClick={handleDivClick}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => { e.preventDefault(); setIsDragging(false); processFiles(Array.from(e.dataTransfer.files)); }}
        className={`border border-dashed border-secondary rounded-xl cursor-pointer h-32 flex flex-col items-center justify-center p-4 transition-all
            bg-lightGray
            ${isDragging ? 'lg:bg-secondary' : 'lg:hover:bg-secondary'}
        `}
      >
        <img src={clip} className="h-5 w-auto mb-1" alt="Clip" />
        <span className="text-sm text-darkGray font-medium">{t('FORM.DOCUMENTS.DROP_TEXT')}</span>
        <input type="file" ref={fileInputRef} onChange={(e) => processFiles(Array.from(e.target.files))} className="hidden" multiple accept=".pdf" />
      </div>

      {files.length > 0 && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:flex lg:flex-wrap gap-y-4 md:gap-x-10 lg:gap-4">
          {files.map((file) => {
            const isActive = file.id === activeDocId;
            return (
              <div key={file.id} className="flex items-center gap-2 w-full md:col-span-1 md:last:odd:col-span-2 lg:w-auto">
                <div
                  onClick={() => handleFileClick(file)}
                  className={`
                    flex items-center justify-between flex-1 lg:flex-none gap-3 px-4 py-2 rounded-lg border transition-all shadow-sm cursor-pointer select-none
                    ${isActive
                      ? "lg:bg-primary lg:border-primary lg:text-white bg-white border-secondary"
                      : "bg-white border-secondary text-darkGray lg:hover:bg-secondary"}
                  `}
                >
                  <span className="text-sm font-semibold truncate pointer-events-none">
                    {file.name}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => removeFile(e, file.id)}
                    className={`font-semibold text-sm ml-1 flex-shrink-0 ${
                      isActive ? 'lg:text-white text-darkGray' : 'text-darkGray'
                    }`}
                  >
                    ✕
                  </button>
                </div>

                <div className="lg:hidden flex-shrink-0">
                  <button
                    type="button"
                    onClick={(e) => handleMobileDownload(e, file)}
                    className="p-2 text-darkGray rounded-lg hover:bg-secondary shadow-sm bg-lightGray flex items-center justify-center"
                  >
                    <Download size={20} strokeWidth={2.5} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default DocumentUpload;